import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Coins, DollarSign, Briefcase, Zap, LucideIcon } from 'lucide-react';
import CountUp from 'react-countup';
import Card from '../components/Card';
import { PermissionGate } from '../components/PermissionGate';
import { UpgradeNudge } from '../components/UpgradeNudge';
import PriceChange from '../components/PriceChange';
import MiniSparkline from '../components/MiniSparkline';
import MarketHeatmap from '../components/MarketHeatmap';
import SignalBadge, { SignalSummary } from '../components/SignalBadge';
import SignalRow from '../components/SignalRow';
import DashboardSignalCard from '../components/DashboardSignalCard';
import AIRecommendationBadge from '../components/AIRecommendationBadge';
import { MarketPulseSkeleton, HotMoversSkeleton, WatchlistSkeleton, SignalsSkeleton } from '../components/Skeleton';
import { useMarketPulse, useHotMovers, useScreenerSignals } from '../hooks/useMarketData';
import { formatCurrency } from '../utils/helpers';
import type { PortfolioAllocation } from '../types';
import type { SignalType } from '../services/api';
import api from '../services/api';
import eodhd from '../services/eodhd';
import { usePermission } from '../hooks/usePermission';
import RecommendedVideos from '../components/knowledge/RecommendedVideos';
import ActivityFeed from '../components/ActivityFeed';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';

type AssetType = 'stock' | 'crypto' | 'etf' | 'forex';

interface HotMover {
  symbol: string;
  name: string;
  change: number;
  type: AssetType;
}

interface AssetTypeIconProps {
  type: AssetType;
}

const AssetTypeIcon: React.FC<AssetTypeIconProps> = ({ type }) => {
  const icons: Record<AssetType, React.ReactNode> = {
    stock: <BarChart3 size={14} className="text-blue-400" />,
    crypto: <Coins size={14} className="text-amber-400" />,
    etf: <Briefcase size={14} className="text-emerald-400" />,
    forex: <DollarSign size={14} className="text-purple-400" />,
  };
  return <>{icons[type] || <BarChart3 size={14} />}</>;
};

interface PortfolioData {
  totalValue: number;
  dailyChange: number;
  dailyChangePercent: number;
  totalReturn: number;
  allocation: PortfolioAllocation[];
}

interface HoldingDisplay {
  id?: number;
  symbol: string;
  name: string;
  quantity: number;
  currentPrice: number;
  avgCost: number;
  value: number;
  pnl: number;
  pnlPercent: number;
  change: number;
  yesterdayChange?: number;
  currency: string;
  signal?: SignalType;
  signalConfidence?: number;
  aiRecommendation?: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';
  aiConfidence?: number;
}

const isSignalVisibilityAllowed = (user: ReturnType<typeof useAuth>['user']) => {
  if (!user?.fais_cat_ii_compliant) return false;
  if (!user.fais_cat_ii_compliance_valid_until) return true;

  const rawExpiry = user.fais_cat_ii_compliance_valid_until;
  const expiry = new Date(rawExpiry);
  if (Number.isNaN(expiry.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return expiry >= today;
};

interface AssetTab {
  id: string;
  label: string;
  icon: LucideIcon;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const canViewSignalDirections = isSignalVisibilityAllowed(user);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('stocks');
  const [signalFilter, setSignalFilter] = useState<SignalType | null>(null);
  const [gainersPage, setGainersPage] = useState(0);
  const [losersPage, setLosersPage] = useState(0);
  const [signalsPage, setSignalsPage] = useState(0);
  const [portfolioData, setPortfolioData] = useState<PortfolioData>({
    totalValue: 0,
    dailyChange: 0,
    dailyChangePercent: 0,
    totalReturn: 0,
    allocation: [],
  });
  const [holdings, setHoldings] = useState<HoldingDisplay[]>([]);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [analyzingSymbol, setAnalyzingSymbol] = useState<string | null>(null);

  // Pre-fetch feed so card auto-hides when empty (shared cache with ActivityFeed)
  const { data: feedEvents = [], isFetched: feedFetched } = useQuery({
    queryKey: ['activity-feed', 15, 8],
    queryFn: () => api.getActivityFeed(15, 8),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  // Use custom hooks for progressive data loading with caching
  const { data: marketPulseData, loading: marketPulseLoading } = useMarketPulse();
  const { data: hotMoversData, loading: hotMoversLoading } = useHotMovers();
  const { data: signalsData, summary: signalsSummary, loading: signalsLoading } = useScreenerSignals({
    filterType: signalFilter || undefined,
  });

  // Provide default empty values to avoid null checks
  const marketPulse = marketPulseData || [];
  const hotMovers = hotMoversData || { gainers: [], losers: [] };
  const signals = signalsData || [];

  const { allowed: hasStrategyPermission } = usePermission('feature:strategy-driven-signals');

  // Persist strategy name across signal filter changes.
  // When signalFilter is active, the backend only returns filtered signals — strategy-matched
  // signals of other types disappear from the set. We keep the last detected name so the
  // personalised header doesn't flicker away when the user switches filter tabs.
  const lastStrategyNameRef = useRef<string | null>(null);
  const detectedStrategyName = hasStrategyPermission
    ? signals.find((s) => s.strategy_match && s.matched_strategy)?.matched_strategy ?? null
    : null;
  if (detectedStrategyName) {
    lastStrategyNameRef.current = detectedStrategyName;
  }
  const activeStrategyName = hasStrategyPermission
    ? (detectedStrategyName ?? lastStrategyNameRef.current)
    : null;

  const assetTabs: AssetTab[] = [
    { id: 'stocks', label: 'Stocks', icon: BarChart3 },
    { id: 'etfs', label: 'ETFs', icon: Briefcase },
    { id: 'crypto', label: 'Crypto', icon: Coins },
    { id: 'forex', label: 'Forex', icon: DollarSign },
    { id: 'commodities', label: 'Gold', icon: Zap },
  ];

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    // Navigate to markets page with the selected asset type filter
    navigate(`/markets?type=${tabId}`);
  };

  // Load portfolio data from backend
  useEffect(() => {
    loadPortfolioData();
  }, []);

  // Reset signals page when filter changes
  useEffect(() => {
    setSignalsPage(0);
  }, [signalFilter]);

  const loadPortfolioData = async () => {
    try {
      setPortfolioLoading(true);

      // Fetch portfolios from backend
      const portfolios = await api.getPortfolios();

      // If no portfolio exists, show empty state
      if (!portfolios || portfolios.length === 0) {
        setPortfolioData({
          totalValue: 0,
          dailyChange: 0,
          dailyChangePercent: 0,
          totalReturn: 0,
          allocation: [],
        });
        setPortfolioLoading(false);
        return;
      }

      // Use first portfolio
      const portfolio = portfolios[0];

      // Load holdings with current prices, signals, and AI recommendations
      const holdingPromises = portfolio.holdings.map(async (h) => {
        try {
          const data = await eodhd.getRealTimePrice(h.symbol);
          const currentPrice = data.close || 0;
          const value = currentPrice * Number(h.quantity);
          const cost = Number(h.avg_cost) * Number(h.quantity);
          const pnl = value - cost;
          const pnlPercent = cost > 0 ? (pnl / cost) * 100 : 0;
          const change = data.change_percent ?? data.change_p ?? 0;
          const dayChange = (value * change) / 100;

          // Get yesterday's change from backend (already calculated)
          const yesterdayChange = data.yesterday_change ?? 0;

          // Fetch signal recommendation
          let signal: SignalType | undefined;
          let signalConfidence: number | undefined;
          try {
            const signalData = await api.getSignal(h.symbol);
            signal = signalData.signal;
            signalConfidence = signalData.confidence;
          } catch (err) {
            console.error(`Failed to fetch signal for ${h.symbol}:`, err);
          }

          // Fetch AI recommendation
          let aiRecommendation: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell' | undefined;
          let aiConfidence: number | undefined;
          try {
            const aiData = await api.getLatestAIAnalysis(h.symbol);
            aiRecommendation = aiData.recommendation;
            aiConfidence = aiData.confidence;
          } catch (err) {
            // Silently fail - AI recommendation is optional
            console.debug(`No AI recommendation for ${h.symbol}`);
          }

          // Determine asset type from symbol
          let assetType = 'Stocks';
          if (h.symbol.includes('.FOREX')) assetType = 'Forex';
          else if (h.symbol.includes('.CC')) assetType = 'Crypto';
          else if (h.symbol.toLowerCase().includes('etf')) assetType = 'ETFs';
          else if (h.symbol.toLowerCase().includes('gold') || h.symbol.toLowerCase().includes('gld')) assetType = 'Gold';

          return {
            id: h.id,
            symbol: h.symbol,
            name: h.symbol.split('.')[0],
            quantity: Number(h.quantity),
            currentPrice,
            avgCost: Number(h.avg_cost),
            value,
            cost,
            pnl,
            pnlPercent,
            dayChange,
            change,
            yesterdayChange,
            assetType,
            currency: h.symbol.includes('JSE') ? 'R' : '$',
            signal,
            signalConfidence,
            aiRecommendation,
            aiConfidence,
          };
        } catch (error) {
          console.error(`Error loading holding ${h.symbol}:`, error);
          const value = Number(h.avg_cost) * Number(h.quantity);
          return {
            id: h.id,
            symbol: h.symbol,
            name: h.symbol.split('.')[0],
            quantity: Number(h.quantity),
            currentPrice: Number(h.avg_cost),
            avgCost: Number(h.avg_cost),
            value,
            cost: value,
            pnl: 0,
            pnlPercent: 0,
            dayChange: 0,
            change: 0,
            yesterdayChange: 0,
            assetType: 'Stocks',
            currency: h.symbol.includes('JSE') ? 'R' : '$',
          };
        }
      });

      const holdings = await Promise.all(holdingPromises);

      // Calculate totals
      const totalValue = holdings.reduce((sum, h) => sum + h.value, 0);
      const totalCost = holdings.reduce((sum, h) => sum + h.cost, 0);
      const totalPnL = totalValue - totalCost;
      const dailyChange = holdings.reduce((sum, h) => sum + h.dayChange, 0);
      const dailyChangePercent = totalValue > 0 ? (dailyChange / totalValue) * 100 : 0;
      const totalReturn = totalCost > 0 ? (totalPnL / totalCost) * 100 : 0;

      // Calculate allocation by asset type
      const allocationMap = new Map<string, { value: number; color: string }>();
      holdings.forEach((h) => {
        const current = allocationMap.get(h.assetType) || { value: 0, color: '' };
        allocationMap.set(h.assetType, {
          value: current.value + h.value,
          color: current.color,
        });
      });

      // Define colors for each asset type
      const colorMap: Record<string, string> = {
        'Stocks': '#3B82F6',
        'ETFs': '#10B981',
        'Crypto': '#F59E0B',
        'Gold': '#EAB308',
        'Forex': '#8B5CF6',
      };

      const allocation: PortfolioAllocation[] = Array.from(allocationMap.entries())
        .map(([name, data]) => ({
          name,
          percent: totalValue > 0 ? (data.value / totalValue) * 100 : 0,
          color: colorMap[name] || '#64748B',
        }))
        .filter((a) => a.percent > 0)
        .sort((a, b) => b.percent - a.percent);

      setPortfolioData({
        totalValue,
        dailyChange,
        dailyChangePercent,
        totalReturn,
        allocation,
      });

      // Store holdings for display
      setHoldings(holdings.map(h => ({
        id: h.id,
        symbol: h.symbol,
        name: h.name,
        quantity: h.quantity,
        currentPrice: h.currentPrice,
        avgCost: h.avgCost,
        value: h.value,
        pnl: h.pnl,
        pnlPercent: h.pnlPercent,
        change: h.change,
        yesterdayChange: h.yesterdayChange,
        currency: h.currency,
        signal: h.signal,
        signalConfidence: h.signalConfidence,
        aiRecommendation: h.aiRecommendation,
        aiConfidence: h.aiConfidence,
      })));
    } catch (error) {
      console.error('Error loading portfolio data:', error);
      // Keep default empty state
    } finally {
      setPortfolioLoading(false);
    }
  };

  // Pagination logic for hot movers
  const ITEMS_PER_PAGE = 4;
  const MAX_PAGES = 3;

  const paginateMovers = (movers: HotMover[], currentPage: number) => {
    const totalPages = Math.min(Math.ceil(movers.length / ITEMS_PER_PAGE), MAX_PAGES);
    const startIndex = currentPage * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      items: movers.slice(startIndex, endIndex),
      totalPages,
      hasMore: movers.length > ITEMS_PER_PAGE
    };
  };

  const paginatedGainers = paginateMovers(hotMovers.gainers, gainersPage);
  const paginatedLosers = paginateMovers(hotMovers.losers, losersPage);

  // Pagination for Trading Signals
  const SIGNALS_PER_PAGE = 5;
  const MAX_SIGNAL_PAGES = 3;

  const paginatedSignals = (() => {
    const totalPages = Math.min(Math.ceil(signals.length / SIGNALS_PER_PAGE), MAX_SIGNAL_PAGES);
    const startIndex = signalsPage * SIGNALS_PER_PAGE;
    const endIndex = startIndex + SIGNALS_PER_PAGE;
    return {
      items: signals.slice(startIndex, endIndex),
      totalPages,
      hasMore: signals.length > SIGNALS_PER_PAGE
    };
  })();

  const triggerAIAnalysis = async (e: React.MouseEvent, symbol: string) => {
    e.stopPropagation();
    if (analyzingSymbol === symbol) return;
    setAnalyzingSymbol(symbol);
    try {
      const result = await api.getAIAnalysis(symbol, 'comprehensive');
      setHoldings(prev => prev.map(h =>
        h.symbol === symbol
          ? { ...h, aiRecommendation: result.recommendation, aiConfidence: result.confidence }
          : h
      ));
    } catch (error) {
      console.error(`AI analysis failed for ${symbol}:`, error);
    } finally {
      setAnalyzingSymbol(null);
    }
  };

  return (
    <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-0">
      <SEO
        title="Dashboard"
        description="Your AI-powered investment dashboard — portfolio overview, trading signals, and market analytics."
        noIndex
      />
      {/* Portfolio Summary - stat cards responsive grid */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-800/50 rounded-xl border border-slate-700/50 p-4 lg:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
          <div>
            <p className="text-sm text-slate-400 mb-1">Portfolio Value</p>
            <p className="text-3xl font-bold font-mono">
              {portfolioLoading ? (
                formatCurrency(0)
              ) : (
                <CountUp
                  end={portfolioData.totalValue}
                  prefix="$"
                  separator=","
                  decimals={2}
                  duration={1.2}
                  preserveValue
                />
              )}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-400 mb-1">Daily P&L</p>
            <p
              className={`text-2xl font-bold font-mono ${
                portfolioData.dailyChange >= 0 ? 'text-emerald-400' : 'text-red-400'
              }`}
            >
              {portfolioData.dailyChange >= 0 ? '▲' : '▼'}{' '}
              {portfolioLoading ? (
                formatCurrency(0)
              ) : (
                <CountUp
                  end={Math.abs(portfolioData.dailyChange)}
                  prefix="$"
                  separator=","
                  decimals={2}
                  duration={1.2}
                  preserveValue
                />
              )}
            </p>
            <PriceChange value={portfolioData.dailyChangePercent} size="sm" />
          </div>
          <div>
            <p className="text-sm text-slate-400 mb-1">Total Return (YTD)</p>
            <p className={`text-2xl font-bold font-mono ${portfolioData.totalReturn >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {portfolioData.totalReturn >= 0 ? '▲' : '▼'}{' '}
              {portfolioLoading ? (
                '0.00%'
              ) : (
                <CountUp
                  end={Math.abs(portfolioData.totalReturn)}
                  suffix="%"
                  decimals={2}
                  duration={1.2}
                  preserveValue
                />
              )}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-400 mb-2">Allocation</p>
            <div className="flex h-3 rounded-full overflow-hidden">
              {portfolioData.allocation.map((item, i) => (
                <div
                  key={i}
                  style={{ width: `${item.percent}%`, backgroundColor: item.color }}
                  className="first:rounded-l-full last:rounded-r-full"
                />
              ))}
            </div>
            <div className="flex gap-3 mt-2 text-xs flex-wrap">
              {portfolioData.allocation.map((item, i) => (
                <div key={i} className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-slate-400">{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recommended Videos widget (AC1–4, 7–8) */}
      <RecommendedVideos />

      {/* Market Pulse - loads independently with skeleton */}
      <Card title="Market Pulse" icon="📈">
        {marketPulseLoading ? (
          <MarketPulseSkeleton />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {marketPulse.map((item, i) => (
              <div key={i} className="flex flex-col items-center">
                <p className="text-xs text-slate-500 mb-1 text-center">{item.label}</p>
                <p className="font-mono font-semibold text-sm">{item.value}</p>
                <PriceChange value={item.change} size="sm" />
                <MiniSparkline positive={item.change >= 0} />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Market Heatmap - renders with its own loading state */}
      <PermissionGate permission="feature:market-heatmap" fallback={<UpgradeNudge tier="pro" features={['Sector performance heatmap', 'Live market breadth view']} />}>
        <Card title="Sector Performance Heatmap" icon="🗺️" action="View Markets" onActionClick={() => navigate('/markets')}>
          <MarketHeatmap showStocks={true} />
        </Card>
      </PermissionGate>

      {/* Asset Type Tabs - Quick navigation to Markets */}
      <div className="flex gap-1 lg:gap-2 p-1 bg-slate-800 rounded-lg overflow-x-auto">
        {assetTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab.id)}
            className={`flex items-center gap-2 px-3 lg:px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap min-h-[44px]
              ${
                activeTab === tab.id
                  ? 'bg-teal-500/20 text-teal-400'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
              }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Two Column Layout - stacks on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hot Movers - loads independently with skeleton */}
        <Card title="🔥 Hot Movers" action="View All" onActionClick={() => navigate('/markets')}>
          {hotMoversLoading ? (
            <HotMoversSkeleton />
          ) : (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="text-xs text-emerald-400 font-semibold mb-3 uppercase">Top Gainers</h4>
                <div className="space-y-3">
                  {paginatedGainers.items.map((item, i) => (
                    <div
                      key={i}
                      onClick={() => navigate(`/analysis?symbol=${item.symbol}`)}
                      className="flex items-center justify-between py-2 px-2 -mx-2 border-b border-slate-700/50 last:border-0 hover:bg-slate-700/30 rounded cursor-pointer transition-colors min-h-[44px]"
                    >
                      <div className="flex items-center gap-2">
                        <AssetTypeIcon type={item.type} />
                        <div>
                          <p className="font-medium text-sm">{item.symbol.split('.')[0]}</p>
                          <p className="text-xs text-slate-500">{item.name}</p>
                        </div>
                      </div>
                      <PriceChange value={item.change} size="sm" />
                    </div>
                  ))}
                </div>
                {paginatedGainers.hasMore && (
                  <div className="flex justify-center gap-2 mt-4">
                    {Array.from({ length: paginatedGainers.totalPages }).map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setGainersPage(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          gainersPage === idx ? 'bg-emerald-400 w-4' : 'bg-slate-600 hover:bg-slate-500'
                        }`}
                        aria-label={`Page ${idx + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div>
                <h4 className="text-xs text-red-400 font-semibold mb-3 uppercase">Top Losers</h4>
                <div className="space-y-3">
                  {paginatedLosers.items.map((item, i) => (
                    <div
                      key={i}
                      onClick={() => navigate(`/analysis?symbol=${item.symbol}`)}
                      className="flex items-center justify-between py-2 px-2 -mx-2 border-b border-slate-700/50 last:border-0 hover:bg-slate-700/30 rounded cursor-pointer transition-colors min-h-[44px]"
                    >
                      <div className="flex items-center gap-2">
                        <AssetTypeIcon type={item.type} />
                        <div>
                          <p className="font-medium text-sm">{item.symbol.split('.')[0]}</p>
                          <p className="text-xs text-slate-500">{item.name}</p>
                        </div>
                      </div>
                      <PriceChange value={item.change} size="sm" />
                    </div>
                  ))}
                </div>
                {paginatedLosers.hasMore && (
                  <div className="flex justify-center gap-2 mt-4">
                    {Array.from({ length: paginatedLosers.totalPages }).map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setLosersPage(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          losersPage === idx ? 'bg-red-400 w-4' : 'bg-slate-600 hover:bg-slate-500'
                        }`}
                        aria-label={`Page ${idx + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Screener Signals - loads with real data from API */}
        <Card
          title={
            activeStrategyName ? (
              <span className="flex items-center gap-2">
                📊 Trading Signals
                <span className="text-teal-400 font-normal normal-case tracking-normal text-xs">
                  via {activeStrategyName}
                </span>
              </span>
            ) : (
              '📊 Trading Signals'
            )
          }
          action="View All Signals"
          onActionClick={() => navigate('/screener')}
        >
          {signalsLoading ? (
            <SignalsSkeleton />
          ) : (
            <div className="space-y-4">
              {/* Signal Summary */}
              {canViewSignalDirections ? (
                <SignalSummary
                  buyCount={signalsSummary.buyCount}
                  holdCount={signalsSummary.holdCount}
                  sellCount={signalsSummary.sellCount}
                  onFilterClick={setSignalFilter}
                  activeFilter={signalFilter}
                />
              ) : (
                <p className="rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 px-3 py-2 text-sm">
                  Signal directions are hidden. Complete FAIS CAT II compliance in Settings to view them.
                </p>
              )}

              {/* Signal List */}
              <div className="space-y-2 pt-2">
                {paginatedSignals.items.map((signal, i) =>
                  hasStrategyPermission && signal.strategy_breakdown?.length ? (
                    <DashboardSignalCard
                      key={i}
                      showSignals={canViewSignalDirections}
                      symbol={signal.symbol}
                      price={signal.price}
                      change_percent={signal.change_percent}
                      previousClose={signal.previousClose}
                      signal={signal.signal}
                      strength={signal.strength}
                      confidence={signal.confidence}
                      strategy_breakdown={signal.strategy_breakdown}
                      match_score={signal.match_score}
                      strategies_evaluated={signal.strategies_evaluated}
                      strategies_matched={signal.strategies_matched}
                      convergence_count={signal.convergence_count}
                      convergence_boost={signal.convergence_boost}
                    />
                  ) : (
                    <SignalRow
                      key={i}
                      showSignal={canViewSignalDirections}
                      symbol={signal.symbol}
                      price={signal.price}
                      change_percent={signal.change_percent}
                      previousClose={signal.previousClose}
                      signal={signal.signal}
                      strength={signal.strength}
                      confidence={signal.confidence}
                      size="sm"
                    />
                  )
                )}
                {signals.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No signals available. Try removing filters.
                  </p>
                )}
              </div>

              {/* Pagination Dots */}
              {paginatedSignals.hasMore && (
                <div className="flex justify-center gap-2 mt-4">
                  {Array.from({ length: paginatedSignals.totalPages }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSignalsPage(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        signalsPage === idx ? 'bg-teal-400 w-4' : 'bg-slate-600 hover:bg-slate-500'
                      }`}
                      aria-label={`Page ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* AI Insight Stream — hidden when empty, premium only */}
      {(!feedFetched || feedEvents.length > 0) && (
        <PermissionGate permission="feature:banker">
          <Card title="⚡ AI Insight Stream" icon="">
            <ActivityFeed limit={15} hours={8} maxHeight="320px" />
          </Card>
        </PermissionGate>
      )}

      {/* Portfolio Holdings - loads with portfolio data */}
      <Card title="💼 Portfolio Holdings" action="View Portfolio" onActionClick={() => navigate('/portfolio')}>
        {portfolioLoading ? (
          <WatchlistSkeleton />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase border-b border-slate-700/50">
                  <th className="pb-3 font-medium">Symbol</th>
                  <th className="pb-3 font-medium text-right">Quantity</th>
                  <th className="pb-3 font-medium text-right">Price</th>
                  <th className="pb-3 font-medium text-right">Value</th>
                  <th className="pb-3 font-medium text-right">P&L</th>
                  <th className="pb-3 font-medium text-right">Yesterday's Change</th>
                  <th className="pb-3 font-medium text-right">Today</th>
                  <th className="pb-3 font-medium text-center">Action</th>
                  <th className="pb-3 font-medium text-center">AI</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((holding, i) => (
                  <tr
                    key={i}
                    onClick={() => navigate(`/asset/${holding.symbol}`)}
                    className="border-t border-slate-700/50 hover:bg-slate-700/30 cursor-pointer transition-colors"
                  >
                    <td className="py-3">
                      <span className="font-mono font-medium text-teal-400">{holding.name}</span>
                    </td>
                    <td className="py-3 text-right font-mono text-sm">{holding.quantity}</td>
                    <td className="py-3 text-right font-mono text-sm">
                      {holding.currency}
                      {holding.currentPrice.toFixed(2)}
                    </td>
                    <td className="py-3 text-right font-mono text-sm">
                      {holding.currency}
                      {holding.value.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="py-3 text-right">
                      <div
                        className={`font-mono text-sm ${
                          holding.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}
                      >
                        {holding.pnl >= 0 ? '+' : ''}
                        {holding.currency}
                        {holding.pnl.toFixed(2)}
                      </div>
                      <div className="text-xs text-slate-500">
                        {holding.pnlPercent >= 0 ? '+' : ''}
                        {holding.pnlPercent.toFixed(1)}%
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="flex justify-end">
                        <PriceChange value={holding.yesterdayChange || 0} size="sm" />
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="flex justify-end">
                        <PriceChange value={holding.change} size="sm" />
                      </div>
                    </td>
                    <td className="py-3 text-center">
                      {holding.signal ? (
                        <div onClick={(e) => e.stopPropagation()}>
                          <SignalBadge
                            signal={holding.signal}
                            confidence={holding.signalConfidence}
                            size="sm"
                          />
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">Loading...</span>
                      )}
                    </td>
                    <td className="py-3 text-center">
                      {analyzingSymbol === holding.symbol ? (
                        <span className="text-xs text-teal-400 animate-pulse" title="Running AI analysis...">Analyzing...</span>
                      ) : holding.aiRecommendation ? (
                        <button
                          type="button"
                          onClick={(e) => triggerAIAnalysis(e, holding.symbol)}
                          className="bg-transparent border-0 p-0 cursor-pointer opacity-90 hover:opacity-100"
                          title="Click to refresh AI analysis"
                        >
                          <AIRecommendationBadge
                            recommendation={holding.aiRecommendation}
                            confidence={holding.aiConfidence}
                            size="sm"
                            showIcon={false}
                          />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => triggerAIAnalysis(e, holding.symbol)}
                          className="text-xs text-teal-400 hover:text-teal-300 transition-colors"
                          title="Click to run AI analysis"
                        >
                          Analyze
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {holdings.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-slate-500">
                      No holdings yet. Add positions to your portfolio.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Dashboard;
